import React from 'react';
import { createRoot } from 'react-dom/client';
import { configure } from 'mobx';
import { Provider } from 'mobx-react';
import Rollbar from 'rollbar';
import { Provider as RollbarProvider } from '@rollbar/react';
import { Route, Routes, BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client/react';
import App from './App';
import * as stores from './stores';
import * as Klaviyo from './services/Klaviyo';
import { isProd } from './services/Env';
import apolloClient from './services/Apollo';

import './styles/tailwind.css';
import './styles/index.scss';
import './styles/overrides.scss';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

Klaviyo.init();

configure({ enforceActions: 'never' });

const rollbarConfig: Rollbar.Configuration = {
  enabled: isProd(),
  accessToken: '0eb7fe265c584c7eb0da82fc52543e31',
  captureUncaught: true,
  captureUnhandledRejections: true,
  environment: 'production',
  payload: {
    client: {
      javascript: {
        code_version: '1.0.0',
        source_map_enabled: false,
      },
    },
  },
  hostSafeList: ['www.rademar.ee', 'rademar.ee'], // ignore thrid-party library errors
  checkIgnore: (_, args) => {
    // Ignore errors caused by network connection issues.
    if (
      args[0] === 'Network Error' ||
      `${args[0]}`.startsWith('Unreachable hosts')
    ) {
      return true;
    }

    return false;
  },
};

const GTM_ID = 'GTM-T99XR2G';
const GTM_ENV = {
  dev: {
    auth: 'moM8tfg4HKyMn1UMVSNWhg',
    preview: 'env-70',
  },
  live: {
    auth: 'LjFk4X24qjTczwBz5HeSzw',
    preview: 'env-1',
  },
};

const initializeGtm = () => {
  const env = isProd() ? GTM_ENV.live : GTM_ENV.dev;
  const dataLayer = window.dataLayer || [];
  dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });
  window.dataLayer = dataLayer;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}&gtm_auth=${env.auth}&gtm_preview=${env.preview}&gtm_cookies_win=x`;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }

  document.head.appendChild(script);
};

initializeGtm();
const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <RollbarProvider config={rollbarConfig}>
    <ApolloProvider client={apolloClient}>
      <Provider {...stores}>
        <BrowserRouter>
          <Routes>
            <Route path="/*" element={<App />} />
            <Route path="/et/*" element={<App locale="et" />} />
            <Route path="/en/*" element={<App locale="en" />} />
            <Route path="/ru/*" element={<App />} />
            <Route path="/lv/*" element={<App locale="lv" />} />
          </Routes>
        </BrowserRouter>
      </Provider>
    </ApolloProvider>
  </RollbarProvider>,
);
